/*
 * HomeFixer360 - Satellite Roof Measurement Platform
 * Copyright (c) 2025 Guy T. Dunn Sr. / Idea Guy Brands Incorporated
 * All Rights Reserved.
 * 
 * This software is proprietary and confidential. Unauthorized copying, distribution,
 * modification, or use is strictly prohibited.
 * 
 * For licensing inquiries: guy@ideaguybrands.com
 */

import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";

// Public Pages
import UpdatedLandingPage from "./pages/LandingPageNew";
import LeadsLandingPage from "./pages/LeadsLandingPage";
import LoginForm from "./components/auth/LoginForm";
import RegisterForm from "./components/auth/RegisterForm";
import ForgotPasswordForm from "./components/auth/ForgotPasswordForm";
import ResetPasswordForm from "./components/auth/ResetPasswordForm";
import BrandPresence from "./pages/BrandPresence";
import UnderConstruction from "./pages/UnderConstruction";

// Legal Pages
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CookiePolicy from "./pages/CookiePolicy";
import GDPRCompliance from "./pages/GDPRCompliance";

// Partner Program Pages
import PartnerProgram from "./pages/PartnerProgram";
import TestPartner from "./pages/TestPartner";
import PartnerApplication from "./pages/PartnerApplication";

// Payment Pages
import PresaleCheckout from "./pages/PresaleCheckout";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancelled from "./pages/PaymentCancelled";

// Early Access Page
import EarlyAccess from "./pages/EarlyAccess";

// Guaranteed Leads Program Page
import GuaranteedLeads from "./pages/GuaranteedLeads";

// Revenue Leak Audit Lead Magnet Page
import RevenueLeakAudit from "./pages/RevenueLeakAudit";

// Coming Soon Page
import ComingSoon from "./pages/ComingSoon";
import AboutUs from "./pages/AboutUs";
import Tutorial from "./pages/Tutorial";

// Blog Pages
import BlogList from "./pages/BlogList";
import BlogPost from "./pages/BlogPost";

// Profile and Proposals Pages
import ProfileSettings from "./pages/ProfileSettings";
import MessagingSettings from "./pages/dashboard/MessagingSettings";
import ProposalsPage from "./pages/ProposalsPage";
import SubscriptionCheckout from "./pages/SubscriptionCheckout";

// Customer Portal
import CustomerProposal from "./pages/CustomerProposal";
import PublicEstimate from "./pages/PublicEstimate";
import PublicLeadGen from "./pages/PublicLeadGen";

// Public Review & Referral Pages
import PublicReviewPage from "./pages/public/PublicReviewPage";
import PublicReferralPage from "./pages/public/PublicReferralPage";

// InsureTrax Test Page
import TestInsuranceRedirect from "./pages/TestInsuranceRedirect";

// Contact Us Page
import ContactUs from "./pages/ContactUs";

// Demo Videos Page
import DemoVideosPage from "./pages/DemoVideosPage";

// Admin Pages
import AdminCoupons from "./pages/AdminCoupons";
import AdminCRM from "./pages/AdminCRM";
import ReleaseManagement from "./pages/ReleaseManagement";
import SuperAdminUsers from "./pages/SuperAdminUsers";
import AdminAICosts from "./pages/AdminAICosts";
import AdminChangelog from "./pages/dashboard/AdminChangelog";

// UI Components
import OnboardingVideoPopup from "./components/ui/OnboardingVideoPopup";

// Chatbot
import Chatbot from "./components/ui/Chatbot";
import { Toaster } from 'sonner';

// Protected Dashboard
import ProtectedRoute from "./components/auth/ProtectedRoute";
import DashboardLayout from "./components/layout/DashboardLayout";
import MainDashboard from "./components/dashboard/MainDashboard";
import EstimatesList from "./components/dashboard/EstimatesList";
import EstimateDetail from "./components/dashboard/EstimateDetail";
import EstimateEdit from "./components/dashboard/EstimateEdit";
import ClaimsList from "./components/dashboard/ClaimsList";
import NewEstimate from "./components/dashboard/NewEstimate";
import NewEstimateChooser from "./components/dashboard/NewEstimateChooser";
import NewLineItemEstimate from "./components/dashboard/NewLineItemEstimate";
import LineItemEstimateDetail from "./components/dashboard/LineItemEstimateDetail";
import NewClaim from "./components/dashboard/NewClaim";
import ClaimDetail from "./components/dashboard/ClaimDetail";
import LeadGeneration from "./components/dashboard/LeadGeneration";
import EmailManagement from "./components/dashboard/EmailManagement";
import SubmissionDocumentTab from "./components/dashboard/SubmissionDocumentTab";
import MyRates from "./components/dashboard/MyRatesSimplified";
import RatesManager from "./components/dashboard/RatesManager";
import Customers from "./components/dashboard/Customers";
import Account from "./components/dashboard/Account";
import Billing from "./components/dashboard/Billing";
import Users from "./components/dashboard/Users";
import TeamMembers from "./components/dashboard/TeamMembers";
import FieldCam from "./components/dashboard/FieldCam";
import FieldReportsList from "./components/dashboard/FieldReportsList";
import UnifiedEmailFunnels from "./components/dashboard/UnifiedEmailFunnels";
import WebhookManagement from "./components/dashboard/WebhookManagement";
import BroadcastCenter from "./components/dashboard/BroadcastCenter";
import ContractorApplications from "./components/dashboard/ContractorApplications";
import StripeConnect from "./components/dashboard/StripeConnect";
import ContractTemplates from "./components/dashboard/ContractTemplates";
import ContractorLandingPage from "./pages/ContractorLandingPage";
import LeadCRM from "./components/dashboard/LeadCRM";
import LeadSourcesManager from "./components/dashboard/LeadSourcesManager";
import ReviewsReferrals from "./pages/ReviewsReferrals";
import AddOnServices from "./components/dashboard/AddOnServices";
import PlatformSalesPage from "./pages/PlatformSalesPage";

// Public Pages - Instant Estimator
import InstantEstimator from "./pages/InstantEstimator";

// Module Pages
import InsurancePage from "./pages/InsurancePage";

// Placeholder components for other modules
const ProjectsPage = () => (
  <div className="p-6">
    <h1 className="text-2xl font-bold mb-4">Projects Module</h1>
    <p>Project management interface coming soon...</p>
  </div>
);

const CommunicationsPage = () => (
  <div className="p-6">
    <h1 className="text-2xl font-bold mb-4">Communications Module</h1>
    <p>Communications hub interface coming soon...</p>
  </div>
);

const DocumentsPage = () => (
  <div className="p-6">
    <h1 className="text-2xl font-bold mb-4">Documents Module</h1>
    <p>Document management interface coming soon...</p>
  </div>
);

const ReportsPage = () => (
  <div className="p-6">
    <h1 className="text-2xl font-bold mb-4">Reports & Analytics</h1>
    <p>Reporting dashboard coming soon...</p>
  </div>
);

const UsersPage = () => (
  <div className="p-6">
    <h1 className="text-2xl font-bold mb-4">User Management</h1>
    <p>User administration interface coming soon...</p>
  </div>
);

const SettingsPage = () => (
  <div className="p-6">
    <h1 className="text-2xl font-bold mb-4">Settings</h1>
    <p>System settings interface coming soon...</p>
  </div>
);

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<UnderConstruction />} />
            <Route path="/leads" element={<UnderConstruction />} />
            <Route path="/login" element={<LoginForm />} />
            <Route path="/register" element={<RegisterForm />} />
            <Route path="/forgot-password" element={<ForgotPasswordForm />} />
            <Route path="/reset-password" element={<ResetPasswordForm />} />
            <Route path="/brand-presence" element={<UnderConstruction />} />
            
            {/* Legal Pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/gdpr" element={<GDPRCompliance />} />
            
            {/* Partner Program Pages */}
            <Route path="/partner-program" element={<UnderConstruction />} />
            <Route path="/partner-application" element={<UnderConstruction />} />
            
            {/* Payment Pages */}
            <Route path="/presale-checkout" element={<PresaleCheckout />} />
            <Route path="/subscription-checkout" element={<SubscriptionCheckout />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-cancelled" element={<PaymentCancelled />} />
            
            {/* Early Access Page */}
            <Route path="/register" element={<EarlyAccess />} />
            
            {/* Guaranteed Leads Program Page */}
            <Route path="/guaranteed-leads" element={<UnderConstruction />} />
            
            {/* Revenue Leak Audit Lead Magnet Page (not in menu) */}
            <Route path="/revenue-leak-audit" element={<UnderConstruction />} />
            
            {/* Customer Portal - Public proposal viewing */}
            <Route path="/proposal/:token" element={<CustomerProposal />} />
            
            {/* Public Estimate Viewing - For lead generation customers */}
            <Route path="/estimate/:estimateId" element={<PublicEstimate />} />
            
            {/* Public Lead Generation Landing Page */}
            <Route path="/leadgen/:username" element={<PublicLeadGen />} />
            
            {/* Public Review & Referral Pages */}
            <Route path="/review/:campaignId" element={<PublicReviewPage />} />
            <Route path="/refer/:campaignId" element={<PublicReferralPage />} />
            
            {/* Homeowner Self-Service Instant Estimator */}
            <Route path="/instant-estimate/:contractorId" element={<InstantEstimator />} />
            <Route path="/get-estimate/:contractorId" element={<InstantEstimator />} />
            
            {/* Contact Us Page */}
            <Route path="/contact" element={<UnderConstruction />} />
            
            {/* InsureTrax Test Page - Direct redirect to static HTML */}
            <Route path="/test-insurance-claims" element={<TestInsuranceRedirect />} />
            <Route path="/test-insurance-claims.html" element={<TestInsuranceRedirect />} />
            
            {/* Contractor Landing Page for Ads */}
            <Route path="/contractors" element={<UnderConstruction />} />
            <Route path="/for-contractors" element={<UnderConstruction />} />
            
            {/* Demo Videos Page (Gated) */}
            <Route path="/demos" element={<DemoVideosPage />} />
            <Route path="/demo-videos" element={<DemoVideosPage />} />
            
            {/* Coming Soon Pages */}
            <Route path="/features" element={<UnderConstruction />} />
            <Route path="/api-documentation" element={<UnderConstruction />} />
            <Route path="/integration-guide" element={<UnderConstruction />} />
            <Route path="/system-status" element={<UnderConstruction />} />
            <Route path="/about" element={<UnderConstruction />} />
            <Route path="/tutorial" element={<UnderConstruction />} />
            <Route path="/careers" element={<UnderConstruction />} />
            <Route path="/press" element={<UnderConstruction />} />
            
            {/* Blog Pages */}
            <Route path="/blog" element={<UnderConstruction />} />
            <Route path="/blog/:slug" element={<UnderConstruction />} />
            
            {/* Protected Dashboard Routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              {/* Main Dashboard */}
              <Route index element={<MainDashboard />} />
              
              {/* Estimates Management */}
              <Route path="estimates" element={<EstimatesList />} />
              <Route path="estimates/new" element={<NewEstimateChooser />} />
              <Route path="estimates/new/satellite" element={<NewEstimate />} />
              <Route path="estimates/new/line-item" element={<NewLineItemEstimate />} />
              <Route path="estimates/line-item/:estimateId" element={<LineItemEstimateDetail />} />
              <Route path="estimates/:estimateId" element={<EstimateDetail />} />
              <Route path="estimates/:estimateId/edit" element={<EstimateEdit />} />
              
              {/* Claims Management */}
              <Route path="claims" element={<ClaimsList />} />
              <Route path="claims/new" element={<NewClaim />} />
              <Route path="claims/:claimId" element={<ClaimDetail />} />
              <Route path="field-cam" element={<FieldCam />} />
              <Route path="field-reports" element={<FieldReportsList />} />
              <Route path="leadgen" element={<LeadGeneration />} />
              <Route path="email-management" element={<EmailManagement />} />
              
              {/* Lead CRM Pipeline */}
              <Route path="crm" element={<LeadCRM />} />
              <Route path="leads" element={<LeadCRM />} />
              <Route path="lead-sources" element={<LeadSourcesManager />} />
              
              <Route path="my-rates" element={<MyRates />} />
              <Route path="rate-cards" element={<RatesManager />} />
              
              {/* Stripe Payment Setup */}
              <Route path="stripe" element={<StripeConnect />} />
              
              {/* Contract Templates */}
              <Route path="contracts" element={<ContractTemplates />} />
              
              {/* Reviews & Referrals */}
              <Route path="reviews" element={<ReviewsReferrals />} />
              
              {/* Add-on Services */}
              <Route path="addons" element={<AddOnServices />} />
              
              {/* Customers */}
              <Route path="customers" element={<Customers />} />
              
              {/* Account */}
              <Route path="account" element={<Account />} />
              
              {/* Billing */}
              <Route path="billing" element={<Billing />} />
              
              {/* Users Management */}
              <Route path="users" element={<Users />} />
              
              {/* Insurance Module Routes */}
              <Route path="insurance/*" element={<InsurancePage />} />
              
              {/* Team Members - For all users */}
              <Route path="team" element={<TeamMembers />} />
              
              {/* Profile Settings */}
              <Route path="profile" element={<ProfileSettings />} />
              
              {/* Messaging Settings */}
              <Route path="messaging-settings" element={<MessagingSettings />} />
              
              {/* Proposals Management */}
              <Route path="proposals" element={<ProposalsPage />} />
              
              {/* Admin Routes - Require admin permissions */}
              <Route 
                path="admin/users" 
                element={
                  <ProtectedRoute requiredPermission="users.manage">
                    <SuperAdminUsers />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="admin/coupons" 
                element={
                  <ProtectedRoute requiredPermission="users.manage">
                    <AdminCoupons />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="admin/crm" 
                element={
                  <ProtectedRoute requiredPermission="users.read">
                    <AdminCRM />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="admin/releases" 
                element={
                  <ProtectedRoute requiredPermission="users.manage">
                    <ReleaseManagement />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="admin/ai-costs" 
                element={
                  <ProtectedRoute requiredPermission="users.manage">
                    <AdminAICosts />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="admin/changelog" 
                element={
                  <ProtectedRoute requiredPermission="users.manage">
                    <AdminChangelog />
                  </ProtectedRoute>
                } 
              />
              
              {/* Platform Sales Pipeline - Super Admin Only */}
              <Route 
                path="admin/platform-sales" 
                element={
                  <ProtectedRoute requiredPermission="users.manage">
                    <PlatformSalesPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Other Module Routes */}
              <Route path="email-funnel" element={<UnifiedEmailFunnels />} />
              <Route path="webhooks" element={<WebhookManagement />} />
              <Route path="broadcast" element={<BroadcastCenter />} />
              <Route path="contractor-applications" element={<ContractorApplications />} />
              <Route 
                path="projects/*" 
                element={
                  <ProtectedRoute requiredPermission="projects.read">
                    <ProjectsPage />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="communications/*" 
                element={
                  <ProtectedRoute requiredPermission="communications.read">
                    <CommunicationsPage />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="documents/*" 
                element={
                  <ProtectedRoute requiredPermission="documents.read">
                    <DocumentsPage />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="reports/*" 
                element={
                  <ProtectedRoute requiredPermission="reports.read">
                    <ReportsPage />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="users/*" 
                element={
                  <ProtectedRoute requiredPermission="users.read">
                    <UsersPage />
                  </ProtectedRoute>
                } 
              />
              
              <Route path="settings/*" element={<SettingsPage />} />
            </Route>
            
            {/* Catch all - redirect to landing page */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      
      {/* Global Chatbot */}
      <Chatbot />
      
      {/* Toast Notifications */}
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;